import { Box, Theme } from '@mui/material';
import { AutocompleteCloseReason } from '@mui/material/Autocomplete';
import { SyntheticEvent, useState } from 'react';
import AsyncAutocomplete from '../AsyncAutocomplete/AsyncAutocomplete';
import InlineEditDisplay from '../InlineEditDisplay/InlineEditDisplay';
import { hasSameValues } from '../inlineEdit.utils';
import { InlineEditAutocompleteProps } from './inlineEditAutocomplete.types';

const InlineEditAutocomplete = <T,>(props: InlineEditAutocompleteProps<T>) => {
  const {
    displayLabel,
    source,
    fetcher,
    getOptionLabel,
    initialData,
    formatOptions,
    renderOption,
    isDisabled,
    isMuted,
    disableClearable,
    dropdownMinWidth,
    hasError,
    errorMessage,
    placeholder,
    testId,
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [draftValues, setDraftValues] = useState<string[]>([]);

  const handleStartEditing = () => {
    if (isDisabled) {
      return;
    }

    if (props.multiple) {
      setDraftValues(props.value);
    }

    setIsEditing(true);
  };

  const handleClose = (_event: SyntheticEvent, reason?: AutocompleteCloseReason) => {
    setIsEditing(false);

    if (!props.multiple || reason === 'escape' || hasSameValues(draftValues, props.value)) {
      return;
    }

    props.onConfirm(draftValues);
  };

  if (!isEditing) {
    return (
      <InlineEditDisplay
        isEmpty={!displayLabel}
        isDisabled={Boolean(isDisabled)}
        isMuted={isMuted}
        hasError={hasError}
        errorMessage={errorMessage}
        placeholder={placeholder}
        testId={testId}
        onStartEditing={handleStartEditing}
      >
        {displayLabel}
      </InlineEditDisplay>
    );
  }

  const editStyles = (theme: Theme) => ({
    '& .MuiAutocomplete-inputRoot': {
      ...theme.typography.body2,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.selected,
      borderRadius: `${theme.shape.borderRadiusMd}px`,
    },
    '& .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input':
      {
        padding: theme.spacing(0.5, 0.75),
        height: 'auto',
      },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  });

  if (props.multiple) {
    return (
      <Box sx={editStyles}>
        <AsyncAutocomplete<T, true>
          autoFocus
          open
          multiple
          filterSelectedOptions
          size="small"
          disabled={isDisabled}
          disableClearable={disableClearable}
          dropdownMinWidth={dropdownMinWidth}
          source={{ ...source, eager: true }}
          fetcher={fetcher}
          value={draftValues}
          initialData={initialData}
          formatOptions={formatOptions}
          renderOption={renderOption}
          getOptionLabel={getOptionLabel}
          placeholder={placeholder}
          testId={testId}
          onClose={handleClose}
          onChange={setDraftValues}
        />
      </Box>
    );
  }

  return (
    <Box sx={editStyles}>
      <AsyncAutocomplete<T>
        autoFocus
        open
        size="small"
        disabled={isDisabled}
        disableClearable={disableClearable}
        dropdownMinWidth={dropdownMinWidth}
        source={{ ...source, eager: true }}
        fetcher={fetcher}
        value={props.value}
        initialData={initialData}
        formatOptions={formatOptions}
        renderOption={renderOption}
        getOptionLabel={getOptionLabel}
        placeholder={placeholder}
        testId={testId}
        onClose={() => setIsEditing(false)}
        onChange={(nextValue) => {
          setIsEditing(false);
          props.onConfirm(nextValue ?? null);
        }}
      />
    </Box>
  );
};

export default InlineEditAutocomplete;
